import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import {
  LayoutDashboard,
  Users,
  Shield,
  FileText,
  Link,
  Settings,
  Activity,
  Monitor,
  ArrowLeft,
  Database,
  Menu,
  X,
  ChevronLeft,
} from 'lucide-react';
import { cn } from '@/lib/utils';

export type AdminSection = 
  | 'dashboard' 
  | 'users' 
  | 'groups' 
  | 'templates' 
  | 'datasets'
  | 'onboarding' 
  | 'settings' 
  | 'monitoring'
  | 'kiosk';

interface AdminLayoutProps {
  activeSection: AdminSection;
  onSectionChange: (section: AdminSection) => void;
  children: React.ReactNode;
  userCount?: number;
  groupCount?: number;
}

const NAV_ITEMS: { id: AdminSection; label: string; icon: React.ComponentType<{ className?: string }>; group: string }[] = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, group: 'Overview' },
  { id: 'users', label: 'Users', icon: Users, group: 'Management' },
  { id: 'groups', label: 'Groups', icon: Shield, group: 'Management' },
  { id: 'templates', label: 'Templates', icon: FileText, group: 'Management' },
  { id: 'datasets', label: 'Datasets', icon: Database, group: 'Management' },
  { id: 'onboarding', label: 'Onboarding', icon: Link, group: 'Configuration' },
  { id: 'kiosk', label: 'Kiosk', icon: Monitor, group: 'Configuration' },
  { id: 'settings', label: 'Settings', icon: Settings, group: 'Configuration' },
  { id: 'monitoring', label: 'Monitoring', icon: Activity, group: 'System' },
];

const GROUPS = ['Overview', 'Management', 'Configuration', 'System'];

export const AdminLayout: React.FC<AdminLayoutProps> = ({
  activeSection,
  onSectionChange,
  children,
  userCount,
  groupCount,
}) => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);

  const getCount = (id: AdminSection) => {
    if (id === 'users' && userCount !== undefined) return userCount;
    if (id === 'groups' && groupCount !== undefined) return groupCount;
    return undefined;
  };

  const renderNav = (mobile = false) => (
    <nav className="flex flex-col h-full">
      <div className="p-4 flex items-center justify-between">
        {sidebarOpen && (
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            <span className="font-semibold text-sm">Admin Panel</span>
          </div>
        )}
        {!mobile && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <ChevronLeft className={cn("w-4 h-4 transition-transform", !sidebarOpen && "rotate-180")} />
          </Button>
        )}
        {mobile && (
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setMobileOpen(false)}>
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      <Separator />

      <ScrollArea className="flex-1 py-2">
        {GROUPS.map(group => {
          const items = NAV_ITEMS.filter(i => i.group === group);
          return (
            <div key={group} className="mb-2">
              {sidebarOpen && (
                <div className="px-4 py-1.5">
                  <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{group}</span>
                </div>
              )}
              {items.map(item => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;
                const count = getCount(item.id);
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      onSectionChange(item.id);
                      if (mobile) setMobileOpen(false);
                    }}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors",
                      isActive
                        ? "bg-primary/10 text-primary font-medium border-r-2 border-primary"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50",
                      !sidebarOpen && "justify-center px-2"
                    )}
                    title={!sidebarOpen ? item.label : undefined}
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    {sidebarOpen && (
                      <>
                        <span className="flex-1 text-left">{item.label}</span>
                        {count !== undefined && (
                          <Badge variant="secondary" className="text-[10px] h-5 px-1.5">{count}</Badge>
                        )}
                      </>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })}
      </ScrollArea>

      <Separator />
      <div className="p-3">
        <button
          onClick={() => navigate('/')}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2 text-sm text-muted-foreground hover:text-foreground rounded-md hover:bg-muted/50 transition-colors",
            !sidebarOpen && "justify-center px-2"
          )}
        >
          <ArrowLeft className="w-4 h-4 shrink-0" />
          {sidebarOpen && <span>Back to Editor</span>}
        </button>
      </div>
    </nav>
  );

  return (
    <div className="min-h-screen bg-workspace flex">
      {/* Desktop sidebar */}
      <aside className={cn(
        "hidden md:flex flex-col border-r border-border bg-card transition-all duration-200 shrink-0",
        sidebarOpen ? "w-56" : "w-14"
      )}>
        {renderNav()}
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-background/80" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-card border-r border-border shadow-lg z-10">
            {renderNav(true)}
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile header */}
        <header className="md:hidden h-14 border-b border-border bg-card flex items-center px-4 gap-3">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setMobileOpen(true)}>
            <Menu className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            <span className="font-semibold">Admin</span>
          </div>
        </header>

        <main className="flex-1 overflow-auto p-4 md:p-6 lg:p-8">
          <div className="max-w-6xl mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};
